import { t } from "@lingui/macro";
import type { ResumeDto } from "@reactive-resume/dto";
import { useCallback, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import type { LoaderFunction } from "react-router";
import { redirect } from "react-router";

import { queryClient } from "@/client/libs/query-client";
import { findResumeById } from "@/client/services/resume";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";

export const BuilderPage = () => {
  const frameRef = useBuilderStore((state) => state.frame.ref);
  const setFrameRef = useBuilderStore((state) => state.frame.setRef);

  const resume = useResumeStore((state) => state.resume);
  const title = useResumeStore((state) => state.resume.title);

  const updateResumeInFrame = useCallback(() => {
    const message = { type: "SET_RESUME", payload: resume.data };

    setImmediate(() => {
      frameRef?.contentWindow?.postMessage(message, "*");
    });
  }, [frameRef?.contentWindow, resume.data]);

  useEffect(() => {
    if (!frameRef) return;
    frameRef.addEventListener("load", updateResumeInFrame);
    return () => {
      frameRef.removeEventListener("load", updateResumeInFrame);
    };
  }, [frameRef]);

  useEffect(updateResumeInFrame, [resume.data]);

  return (
    <>
      <Helmet>
        <title>
          {title}
        </title>
      </Helmet>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden pt-12">
        <iframe
          ref={setFrameRef}
          title={resume.id}
          src="/artboard/builder"
          className="h-0 min-h-0 w-full flex-1 rounded-2xl bg-neutral-100"
        />
      </div>
    </>
  );
};

export const builderLoader: LoaderFunction<ResumeDto> = async ({ params }) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const id = params.id!;

    const resume = await queryClient.fetchQuery({
      queryKey: ["resume", { id }],
      queryFn: () => findResumeById({ id }),
    });

    useResumeStore.setState({ resume });
    useResumeStore.temporal.getState().clear();

    return resume;
  } catch (err: any) {
    const status = err?.status ?? err?.response?.status;
    if (status === 401 || status === 403) return redirect("/auth/login");
    if (status === 400 || status === 422) {
      throw new Response(
        JSON.stringify({
          title: "This resume has invalid data",
          message: err?.message ?? "Validation failed",
          id: params.id,
        }),
        { status: 422, headers: { "Content-Type": "application/json" } },
      );
    }
    return redirect("/dashboard");
  }
};
